import React, { useState } from 'react';
import { typeStyle } from '@carvana/showroom-css-theme';
import { useIntersectionObserver } from 'usehooks-ts';
import { LinkWithNativeEvents } from '@components/_common/link/link';
import styles from './vehicle-coverage.module.css';
import { DetailToggle } from '../shared/detail-toggle/detail-toggle';
import { WarrantyData } from './vehicle-coverage';
import { ExpiredLabel, WarrantyHighlight, WarrantyHighlightProps } from './shared';

const LIMITED_WARRANTY_URL = '/repairs';

export type LimitedWarrantyProps = {
  warranty?: WarrantyData;
  onIntersection?: () => void;
  onToggle?: (isOpen: boolean) => void;
  onLinkClick?: (url: string) => void;
};

export const LimitedWarranty = ({ warranty, onIntersection, onToggle, onLinkClick }: LimitedWarrantyProps) => {
  const { ref } = useIntersectionObserver({
    freezeOnceVisible: true,
    onChange: (isIntersecting) => {
      if (isIntersecting && onIntersection) onIntersection();
    },
  });
  const [isExpanded, setIsExpanded] = useState(false);
  const isLimitedWarrantyExpired = !warranty || warranty?.isExpired;

  return (
    <>
      <div className={styles['divider']} />

      <DetailToggle
        id="limited-warranty"
        onDetailsToggle={(val) => {
          setIsExpanded(val);
          return onToggle && onToggle(val);
        }}
        header={
          <span ref={ref}>
            100-Day Limited Warranty
            <ExpiredLabel isExpired={isLimitedWarrantyExpired} />
          </span>
        }
        headerContent={
          <div className={styles['vehicle-coverage-content']}>
            <div className={styles['warranty-text']} style={typeStyle('body-s')}>
              Limited Warranty is included in your purchase, providing peace of mind for unexpected mechanical or
              electrical issues. $100 deductible at in-network facilities and $350 at out-of-network facilities.
            </div>

            {!isExpanded && (
              <WarrantyHighlight
                isExpired={isLimitedWarrantyExpired}
                durationLeft={warranty?.durationLeft}
                totalCoverageInMiles={warranty?.totalCoverageInMiles}
                id="limited-warranty"
                purchaseMileage={warranty?.purchaseMileage}
                currentMileage={warranty?.currentMileage}
                mileageLastUpdated={warranty?.mileageLastUpdated}
              />
            )}
          </div>
        }
      >
        <LimitedWarrantyDetails
          isExpired={isLimitedWarrantyExpired}
          durationLeft={warranty?.durationLeft}
          totalCoverageInMiles={warranty?.totalCoverageInMiles}
          id="limited-warranty"
          purchaseMileage={warranty?.purchaseMileage}
          currentMileage={warranty?.currentMileage}
          mileageLastUpdated={warranty?.mileageLastUpdated}
          onLinkClick={onLinkClick}
        />
      </DetailToggle>
    </>
  );
};

const LimitedWarrantyDetails = ({
  isExpired,
  durationLeft,
  totalCoverageInMiles,
  id,
  purchaseMileage,
  currentMileage,
  mileageLastUpdated,
  onLinkClick,
}: WarrantyHighlightProps & { onLinkClick?: (url: string) => void }) => {
  return (
    <div className={styles['expanded-coverage']} data-testid="limited-warranty-details">
      <div className={styles['coverage']}>
        <WarrantyHighlight
          isExpired={isExpired}
          durationLeft={durationLeft}
          totalCoverageInMiles={totalCoverageInMiles}
          id={id}
          purchaseMileage={purchaseMileage}
          currentMileage={currentMileage}
          mileageLastUpdated={mileageLastUpdated}
        />
      </div>

      <p style={typeStyle('body-s')}>
        Please visit our{' '}
        <LinkWithNativeEvents
          href={LIMITED_WARRANTY_URL}
          data-testid="limited-warranty-link"
          nativeEvent={{
            event: 'link',
            payload: {
              url: LIMITED_WARRANTY_URL,
            },
          }}
          // Capture phase: LinkWithNativeEvents drops onClick on the native-app branch
          onClickCapture={() => onLinkClick?.(LIMITED_WARRANTY_URL)}
        >
          Limited Warranty
        </LinkWithNativeEvents>{' '}
        page for more information.
      </p>

      <div style={typeStyle('body-s')}>
        <p>
          This warranty <span style={typeStyle('header-xs')}>doesn&apos;t cover:</span>
        </p>
        <ul>
          <li>Routine maintenance, like worn tires or brakes</li>
          <li>Cosmetic or accident damage</li>
          <li>Failures resulting from influence or lack of maintenance</li>
        </ul>
      </div>
    </div>
  );
};
